import {
  Viewer,
  DefaultViewerParams,
  SpeckleLoader,
  UrlHelper,
  ViewerEvent,
  LoaderEvent,
  CameraController,
  SelectionExtension,
  FilteringExtension,
  SectionTool,
  SectionOutlines,
  ViewModes,
  ViewMode,
  UpdateFlags
} from '@speckle/viewer';

import { Box3, Vector3 } from 'three';

import {
  streamId,
  // objectId,
  commitId
} from '../speckleConstants.js';

import { ref } from 'vue';

let viewer;
const currentZ = ref(null);

const params = DefaultViewerParams;
params.verbose = true;
// params.environmentSrc = {
//   src: '/environments/sample-hdri.exr',
//   type: 'exr'
// };

function useViewer(container, onProgress) {
  viewer = new Viewer(container, params);

  load(onProgress);

  return viewer;
}

function selectObjects(ids) {
  if (!viewer) return;
  return viewer.getExtension(SelectionExtension).selectObjects(ids);
}

function clearSelection() {
  if (!viewer) return;
  return viewer.getExtension(SelectionExtension).clearSelection();
}

function isolateObjects(...args) {
  if (!viewer) return;
  return viewer.getExtension(FilteringExtension).isolateObjects(...args);
}

function hideObjects(ids) {
  if (!viewer) return;
  return viewer.getExtension(FilteringExtension).hideObjects(ids);
}

function resetFilters() {
  if (!viewer) return;
  return viewer.getExtension(FilteringExtension).resetFilters();
}

function zoomToObjects(ids) {
  if (!viewer) return;
  return viewer.getExtension(CameraController).setCameraView(ids, true);
}

function findNodesById(id) {
  const tree = viewer?.getWorldTree();
  if (!tree || !id) return [];
  return tree.findId(id) || [];
}

function getRawVariants(node) {
  const variants = [
    node?.model?.raw,
    node?.model?.raw?.data,
    node?.raw,
    node?.raw?.data
  ];

  return variants.filter(val => val && typeof val === 'object');
}

function getFirstDefinedValue(values) {
  return values.find(val => val !== undefined && val !== null);
}

function getParameterValue(raw, parameterName) {
  const parameters = raw?.properties?.Parameters;
  if (!parameters) {
    return undefined;
  }

  for (const group of Object.values(parameters)) {
    for (const parameter of Object.values(group || {})) {
      if (parameter?.internalDefinitionName === parameterName || parameter?.name === parameterName) {
        return parameter.value;
      }
    }
  }

  return undefined;
}

function getAssetId(raw) {
  return getFirstDefinedValue([
    raw?.id,
    raw?.speckle_id,
    raw?.applicationId,
    getParameterValue(raw, 'IFC_GUID')
  ]);
}

function getAssetCategory(raw) {
  const parameterCategory = getParameterValue(raw, 'ELEM_CATEGORY_PARAM');

  if (typeof parameterCategory === 'string') {
    return parameterCategory;
  }

  if (typeof parameterCategory === 'object') {
    return parameterCategory?.name || parameterCategory?.value;
  }

  return getFirstDefinedValue([
    raw?.category,
    raw?.type,
    raw?.properties?.category,
    raw?.properties?.type
  ]);
}

function getAssetLevel(raw) {
  const level = getFirstDefinedValue([
    raw?.level,
    raw?.levelId,
    raw?.properties?.level,
    raw?.properties?.levelId,
    getParameterValue(raw, 'LEVEL_PARAM')
  ]);

  if (typeof level === 'object') {
    return level?.id || level?.name || level?.value;
  }

  return level;
}

function getAssetRoomId(raw) {
  const candidate = getFirstDefinedValue([
    raw?.roomId,
    raw?.room?.id,
    raw?.room?.elementId,
    raw?.room?.name,
    raw?.room?.number,
    typeof raw?.room === 'string' || typeof raw?.room === 'number' ? raw?.room : undefined,
    raw?.properties?.roomId,
    getParameterValue(raw, 'ROOM_ID'),
    getParameterValue(raw, 'Room'),
    getParameterValue(raw, 'Room Name'),
    getParameterValue(raw, 'Room Number'),
    getParameterValue(raw, 'ELEM_ROOM_NAME'),
    getParameterValue(raw, 'ELEM_ROOM_NUMBER')
  ]);

  if (candidate && typeof candidate === 'object') {
    return candidate?.id || candidate?.elementId || candidate?.name || candidate?.value;
  }
  return candidate;
}

function normalizeAsset(raw) {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  return {
    ...raw,
    id: getAssetId(raw),
    category: getAssetCategory(raw),
    level: getAssetLevel(raw),
    roomId: getAssetRoomId(raw)
  };
}

function getNormalizedAssetFromNode(node) {
  const variants = getRawVariants(node);

  for (const raw of variants) {
    const normalized = normalizeAsset(raw);
    if (normalized?.id !== undefined) {
      return normalized;
    }
  }

  return normalizeAsset(variants[0]);
}

function getAssetsByRoomId(roomInput) {
  const tree = viewer?.getWorldTree();
  if (!tree) return [];

  const targets = new Set();
  let targetRoomId = null;
  let targetLevel = null;

  if (typeof roomInput === 'object' && roomInput !== null) {
    if (roomInput.id) {
      targets.add(String(roomInput.id));
      targetRoomId = roomInput.id;
    }
    if (roomInput.roomId) {
      targets.add(String(roomInput.roomId));
      if (!targetRoomId) targetRoomId = roomInput.roomId;
    }
    if (roomInput.number) targets.add(String(roomInput.number));
    if (roomInput.name) targets.add(String(roomInput.name));
    if (roomInput.level) targetLevel = roomInput.level;
  } else if (roomInput !== undefined && roomInput !== null) {
    targets.add(String(roomInput));
    targetRoomId = roomInput;
  }

  if (targets.size === 0) return [];

  const exceptCategories = [ 'Areas', 'Rooms', 'Levels', 'Parking', 'Walls', 'Ceilings' ];
  const data = new Map();

  const isNonEmptyCategory = cat => typeof cat === 'string' && cat.trim() !== '';

  // 1. Pass 1: Check explicitly defined room properties/parameters
  tree.walk(node => {
    const asset = getNormalizedAssetFromNode(node);
    if (!asset || !asset.id || !isNonEmptyCategory(asset.category)) return true;

    if (exceptCategories.includes(asset.category)) return true;

    const assetRoomId = asset.roomId;
    if (assetRoomId !== undefined && assetRoomId !== null) {
      if (targets.has(String(assetRoomId))) {
        if (!data.has(asset.id)) {
          data.set(asset.id, asset);
        }
      }
    }
    return true;
  });

  if (data.size > 0) {
    return Array.from(data.values());
  }

  // 2. Pass 2: Spatial bounding box containment fallback
  let roomBox = null;
  if (viewer?.getRenderer && targetRoomId) {
    try {
      const box = viewer.getRenderer().boxFromObjects([ targetRoomId ]);
      if (box && !box.isEmpty()) {
        roomBox = box;
      }
    } catch (e) {
      console.warn('Could not compute room bounding box:', e);
    }
  }

  if (roomBox) {
    tree.walk(node => {
      const asset = getNormalizedAssetFromNode(node);
      if (!asset || !asset.id || !isNonEmptyCategory(asset.category) || data.has(asset.id)) return true;

      if (exceptCategories.includes(asset.category)) return true;

      if (targetLevel && asset.level && asset.level !== targetLevel) {
        return true;
      }

      try {
        const assetBox = viewer.getRenderer().boxFromObjects([ asset.id ]);
        if (assetBox && !assetBox.isEmpty()) {
          const center = assetBox.getCenter(new Vector3());
          if (roomBox.containsPoint(center) || roomBox.intersectsBox(assetBox)) {
            data.set(asset.id, asset);
          }
        }
      } catch (e) {
        // Ignore box errors on non-renderable nodes
      }

      return true;
    });
  }

  return Array.from(data.values());
}

function getAssetsByLevelId(level) {
  const tree = viewer?.getWorldTree();
  if (!tree) return [];

  const exceptCategories = [ 'Areas', 'Rooms', 'Levels', 'Parking', 'Walls', 'Ceilings' ];
  const data = new Map();

  tree.walk(node => {
    const asset = getNormalizedAssetFromNode(node);
    if (
      asset?.level === level &&
      typeof asset?.category === 'string' &&
      asset.category.trim() !== '' &&
      !exceptCategories.includes(asset.category)
    ) {
      if (asset?.id && !data.has(asset.id)) {
        data.set(asset.id, asset);
      }
    }
    return true;
  });

  return Array.from(data.values());
}

function setTopView(ids = null) {
  if (!viewer) return;

  if (!ids) {
    viewer.getExtension(CameraController).setCameraView('top', true);
    return;
  }

  const sections = viewer.getExtension(SectionTool);
  sections.enabled = true;
  sections.visible = false;

  const box = new Box3().copy(viewer.getRenderer().sceneBox);
  const levelBox = viewer.getRenderer().boxFromObjects(ids);
  const z = levelBox.min.z + (levelBox.max.z - levelBox.min.z) * 0.5;

  currentZ.value = z;

  box.max.z = z;
  sections.setBox(box);

  viewer.getExtension(CameraController).disableRotations();

  setTopView();
  zoomToObjects(ids);
}

function setDefaultView() {
  if (!viewer) return;

  const sections = viewer.getExtension(SectionTool);
  sections.enabled = false;
  sections.visible = true;

  currentZ.value = null;

  viewer.getExtension(CameraController).enableRotations();
  viewer.getExtension(CameraController).setCameraView('3d', true);
}

function setViewMode(mode) {
  if (!viewer) return;

  const targetMode = typeof mode === 'number' ? mode : ViewMode[mode];
  if (targetMode !== undefined) {
    viewer.getExtension(ViewModes).setViewMode(targetMode);
    viewer.requestRender(UpdateFlags.RENDER_RESET);
  }
}

async function load(onProgress) {
  await viewer.init();

  viewer.createExtension(CameraController);
  viewer.createExtension(SelectionExtension);
  viewer.createExtension(FilteringExtension);
  viewer.createExtension(ViewModes);
  viewer.createExtension(SectionTool);
  viewer.createExtension(SectionOutlines);

  const urls = await UrlHelper.getResourceUrls(
    `${window.location.origin}/streams/${streamId}/commits/${commitId}`
  );

  const tree = viewer.getWorldTree();
  const total = urls.length;
  const progressByUrl = {};

  for (const url of urls) {
    const loader = new SpeckleLoader(tree, url, '', true);
    loader.on(LoaderEvent.LoadProgress, progress => {
      progressByUrl[url] = progress?.progress ?? 0;

      let _progress = 0;
      Object.values(progressByUrl).forEach(p => {
        _progress += p || 0;
      });
      _progress = parseInt((100 * _progress) / total, 10);

      onProgress(_progress);
    });
    await viewer.loadObject(loader, true); // eslint-disable-line no-await-in-loop
  }
}

function disposeViewer() {
  if (viewer) {
    viewer.dispose();
    viewer = null;
  }
  currentZ.value = null;
}

export {
  useViewer,
  disposeViewer,
  findNodesById,
  ViewerEvent,
  getAssetsByLevelId,
  getAssetsByRoomId,
  selectObjects,
  clearSelection,
  isolateObjects,
  hideObjects,
  resetFilters,
  zoomToObjects,
  ViewMode,
  setViewMode,
  setTopView,
  setDefaultView,
  currentZ
};

